import { toURLSearchParams } from '@wix/sdk-runtime/rest-modules';
import { transformSDKFloatToRESTFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTFloatToSDKFloat } from '@wix/sdk-runtime/transformations/float';
import { transformRESTBytesToSDKBytes } from '@wix/sdk-runtime/transformations/bytes';
import { transformSDKTimestampToRESTTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformRESTTimestampToSDKTimestamp } from '@wix/sdk-runtime/transformations/timestamp';
import { transformSDKFieldMaskToRESTFieldMask } from '@wix/sdk-runtime/transformations/field-mask';
import { transformPaths } from '@wix/sdk-runtime/transformations/transform-paths';
import { resolveUrl } from '@wix/sdk-runtime/rest-modules';
import { ResolveUrlOpts } from '@wix/sdk-runtime/rest-modules';
import { RequestOptionsFactory } from '@wix/sdk-types';

function resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl(
  opts: Omit<ResolveUrlOpts, 'domainToMappings'>
) {
  const domainToMappings = {
    'www._base_domain_': [
      {
        srcPath: '/ecom/v1/checkout-templates',
        destPath: '/v1/checkout-templates',
      },
    ],
    'www.wixapis.com': [
      {
        srcPath: '/ecom/v1/checkout-templates',
        destPath: '/v1/checkout-templates',
      },
    ],
  };

  return resolveUrl(Object.assign(opts, { domainToMappings }));
}

const PACKAGE_NAME = '@wix/auto_sdk_ecom_checkout-templates';

/**
 * Creates a checkout template.
 *
 * A checkout template is used to create a new checkout that will include predefined information. For example, a single link with
 * a `checkoutTemplateId` can be shared with customers and each time the link is clicked, a new checkout page will be created
 * for that customer with certain checkout information already populated.
 *
 * The customizable features include the option to allow or to lock coupon codes or gift cards. For example, if a store owner is
 * using the checkout template to offer a flash sale to their social media followers, they may want to lock the option to apply an
 * additional coupon on top of the sale being offered. If so, they can set `customization.lockedCoupon` to `true`.
 *
 * A checkout can be created with a checkout template by calling `createCheckoutFromTemplate()`. The site may add further customizations to the new checkout and then redirect the customer using the new checkout's `checkoutUrl`.
 */
export function createCheckoutTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __createCheckoutTemplate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
          },
          {
            path: 'checkoutTemplate.customLineItems.physicalProperties.weight',
          },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          {
            path: 'checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
          },
          {
            path: 'checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.CreateCheckoutTemplate',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath: '/v1/checkout-templates',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
              },
              {
                path: 'checkoutTemplate.customLineItems.physicalProperties.weight',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __createCheckoutTemplate;
}

/** Retrieves a checkout template. */
export function getCheckoutTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __getCheckoutTemplate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.GetCheckoutTemplate',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath: '/v1/checkout-templates/{checkoutTemplateId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
              },
              {
                path: 'checkoutTemplate.customLineItems.physicalProperties.weight',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __getCheckoutTemplate;
}

/**
 * Updates a checkout template.
 *
 * If the info in a checkout template is updated, only new checkouts created from this template will include the updated items. Checkouts previously
 * created from this template before the update will not be affected.
 */
export function updateCheckoutTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __updateCheckoutTemplate({ host }: any) {
    const serializedData = transformPaths(payload, [
      {
        transformFn: transformSDKFieldMaskToRESTFieldMask,
        paths: [{ path: 'fieldMask' }],
      },
      {
        transformFn: transformSDKFloatToRESTFloat,
        paths: [
          {
            path: 'checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
          },
          {
            path: 'checkoutTemplate.customLineItems.physicalProperties.weight',
          },
        ],
      },
      {
        transformFn: transformSDKTimestampToRESTTimestamp,
        paths: [
          {
            path: 'checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
          },
          {
            path: 'checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
          },
        ],
      },
    ]);
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'PATCH' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.UpdateCheckoutTemplate',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath: '/v1/checkout-templates/{checkoutTemplate.id}',
        data: serializedData,
        host,
      }),
      data: serializedData,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutTemplate.lineItems.catalogOverrideFields.physicalProperties.weight',
              },
              {
                path: 'checkoutTemplate.customLineItems.physicalProperties.weight',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'checkoutTemplate.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'checkoutTemplate.customLineItems.serviceProperties.scheduledDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __updateCheckoutTemplate;
}

/**
 * Deletes a checkout template.
 *
 * If a checkout template is deleted and a customer attempts to create a checkout with that `checkoutTemplateId` then
 * the customer will be redirected to the domain site.
 */
export function deleteCheckoutTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __deleteCheckoutTemplate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'DELETE' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.DeleteCheckoutTemplate',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath: '/v1/checkout-templates/{checkoutTemplateId}',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
    };

    return metadata;
  }

  return __deleteCheckoutTemplate;
}

/**
 * Creates a query to retrieve a list of checkout templates.
 *
 * The `queryCheckoutTemplates()` function builds a query to retrieve a list of checkout templates and returns a `CheckoutTemplatesQueryBuilder` object.
 *
 * The returned object contains the query definition, which is typically used to run the query using the `find()` function.
 *
 * You can refine the query by chaining `CheckoutTemplatesQueryBuilder` functions onto the query. `CheckoutTemplatesQueryBuilder` functions enable you to sort, filter, and control the results that `queryCheckoutTemplates()` returns.
 *
 * `queryCheckoutTemplates()` runs with the following `CheckoutTemplatesQueryBuilder` default that you can override:
 * + `ascending("_id")`
 *
 * The functions that are chained to `queryCheckoutTemplates()` are applied in the order they are called. For example, if you apply `ascending("status")` and then `ascending("_id")`, the results are sorted first by the `"status"`, and then, if there are multiple results with the same `"status"`, the items are sorted by `"_id"`.
 *
 * The following `CheckoutTemplatesQueryBuilder` functions are supported for the `queryCheckoutTemplates()` function. For a full description of the checkout template object, see the object returned for the `items` property in `CheckoutTemplatesQueryResult`.
 */
export function queryCheckoutTemplates(
  payload: object
): RequestOptionsFactory<any> {
  function __queryCheckoutTemplates({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.QueryCheckoutTemplates',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath: '/v1/checkout-templates/query',
        data: payload,
        host,
      }),
      data: payload,
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTFloatToSDKFloat,
            paths: [
              {
                path: 'checkoutTemplates.lineItems.catalogOverrideFields.physicalProperties.weight',
              },
              {
                path: 'checkoutTemplates.customLineItems.physicalProperties.weight',
              },
            ],
          },
          {
            transformFn: transformRESTTimestampToSDKTimestamp,
            paths: [
              {
                path: 'checkoutTemplates.customLineItems.subscriptionOptionInfo.subscriptionSettings.startDate',
              },
              {
                path: 'checkoutTemplates.customLineItems.serviceProperties.scheduledDate',
              },
            ],
          },
        ]),
    };

    return metadata;
  }

  return __queryCheckoutTemplates;
}

/**
 * Creates a new checkout based on the checkout template.
 *
 * Before using this function, you must have a checkout template available. Create a checkout template with `createCheckoutTemplate()`.
 *
 * The customer can be directed to the new checkout using the checkout's `checkoutUrl`.
 */
export function createCheckoutFromTemplate(
  payload: object
): RequestOptionsFactory<any> {
  function __createCheckoutFromTemplate({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'POST' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.CreateCheckoutFromTemplate',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath:
          '/v1/checkout-templates/{checkoutTemplateId}/create-checkout-from-template',
        data: payload,
        host,
      }),
      data: payload,
    };

    return metadata;
  }

  return __createCheckoutFromTemplate;
}

/**
 * Creates a new checkout based on the checkout template and redirects to the new checkout page.
 *
 * Before using this function, you must have a checkout template available. Create a checkout template with `createCheckoutTemplate()`.
 *
 * To build a URL that uses this function, follow this format:
 * `https://www.wixapis.com/ecom/v1/checkout-templates/{checkoutTemplateId}/create-and-redirect-to-checkout?siteId={siteId}`
 *
 * To create a checkout but not automatically redirect to the checkout page, use `createCheckoutFromTemplate()`.
 */
export function createAndRedirectToCheckout(
  payload: object
): RequestOptionsFactory<any> {
  function __createAndRedirectToCheckout({ host }: any) {
    const metadata = {
      entityFqdn: 'wix.ecom.v1.checkout_template',
      method: 'GET' as any,
      methodFqn:
        'wix.ecom.checkout_template.api.v1.CheckoutTemplateService.CreateAndRedirectToCheckout',
      packageName: PACKAGE_NAME,
      url: resolveWixEcomCheckoutTemplateApiV1CheckoutTemplateServiceUrl({
        protoPath:
          '/v1/checkout-templates/{checkoutTemplateId}/create-and-redirect-to-checkout',
        data: payload,
        host,
      }),
      params: toURLSearchParams(payload),
      transformResponse: (payload: any) =>
        transformPaths(payload, [
          {
            transformFn: transformRESTBytesToSDKBytes,
            paths: [{ path: 'body' }],
          },
        ]),
    };

    return metadata;
  }

  return __createAndRedirectToCheckout;
}
